import { useContext, useEffect, useState } from "react";
import ChatContext from "./ChatContext";
import { useMatrixRoom, useMatrixRoomAliases } from "./useMatrixRoom";
import { extractEmployeeIdFromRoomName, isEmployeeRoomName } from "./roomAlias";

const getRoomNameFromAlias = (alias: string) => {
  // We expect the alias to be of the form: #roomName:domain
  const roomName = alias.split("#")[1]?.split(":")[0];
  return roomName;
};

const useRoomNamesWithUnreadNotifications = () => {
  const { matrixClient, unreadNotifications } = useContext(ChatContext);
  const [roomIds, setRoomIds] = useState<string[]>([]);
  const roomAliasMap = useMatrixRoomAliases(matrixClient, roomIds);
  const roomNames = Object.values(roomAliasMap)
    .flatMap((aliases) => aliases.map(getRoomNameFromAlias))
    .filter((name): name is string => name !== undefined);

  useEffect(() => {
    if (!matrixClient || !unreadNotifications) {
      return;
    }

    const roomsWithNotifications = Object.entries(unreadNotifications).filter(
      ([_, count]) => count > 0,
    );

    // Acceptable use, we just sync once when the matrix client is made available,
    // and once every time the unread notifications changes
    // oxlint-disable-next-line react/set-state-in-effect
    setRoomIds(roomsWithNotifications.map(([roomId]) => roomId));
  }, [matrixClient, unreadNotifications]);

  return roomNames;
};

const useUnreadNotificationCounts = ({
  roomName,
}: {
  roomName: string | undefined;
}) => {
  const { matrixClient, unreadNotifications } = useContext(ChatContext);
  const roomAlias = `#${roomName}:${matrixClient?.getDomain()}`;
  const roomId = useMatrixRoom(matrixClient, roomAlias)?.roomId;
  if (!roomId) {
    return undefined;
  }

  return unreadNotifications?.[roomId];
};

/**
 * Pure counting logic, separated from the hook so it can be unit-tested.
 *
 * Returns `undefined` while `unreadNotifications` is still loading (i.e. undefined),
 * and a number (≥ 0) once data has arrived — even when the count is 0.
 */
export const countEmployeeUnreadNotifications = ({
  unreadNotifications,
  roomAliasMap,
  employeeId,
  env,
  organization,
}: {
  unreadNotifications: Record<string, number> | undefined;
  roomAliasMap: Record<string, string[]>;
  employeeId: string;
  env: string;
  organization: string;
}): number | undefined => {
  // Return undefined (still loading) until unreadNotifications has been populated
  if (unreadNotifications === undefined) {
    return undefined;
  }

  let totalCount = 0;

  for (const [roomId, aliases] of Object.entries(roomAliasMap)) {
    for (const alias of aliases) {
      const roomName = getRoomNameFromAlias(alias);
      if (!roomName) {
        continue;
      }
      if (!isEmployeeRoomName(roomName, organization)) {
        continue;
      }
      const extracted = extractEmployeeIdFromRoomName(roomName, env);
      if (extracted === employeeId) {
        totalCount += unreadNotifications[roomId] ?? 0;
        break; // Only count each room once
      }
    }
  }

  return totalCount;
};

const useEmployeeUnreadNotificationCount = ({
  employeeId,
  env,
  organization,
}: {
  employeeId: string;
  env: string;
  organization: string;
}): number | undefined => {
  const { matrixClient, unreadNotifications } = useContext(ChatContext);

  const roomIdsWithUnread = Object.entries(unreadNotifications ?? {})
    .filter(([_, count]) => count > 0)
    .map(([roomId]) => roomId);

  const roomAliasMap = useMatrixRoomAliases(matrixClient, roomIdsWithUnread);

  return countEmployeeUnreadNotifications({
    unreadNotifications,
    roomAliasMap,
    employeeId,
    env,
    organization,
  });
};

const useHasUnreadNotifications = ({
  roomName,
}: {
  roomName: string | undefined;
}) => {
  const unreadNotificationCount = useUnreadNotificationCounts({ roomName });
  return unreadNotificationCount !== undefined && unreadNotificationCount >= 1;
};

export {
  useUnreadNotificationCounts,
  useEmployeeUnreadNotificationCount,
  useHasUnreadNotifications,
  useRoomNamesWithUnreadNotifications,
};
